/**
 * Eid cupcake toppers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_ramadan_eid`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const eidCupcakeToppersPack: ToilePack = {
    slug: 'eid-cupcake-toppers',
    category: 'season',
    name: 'Eid cupcake toppers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'rounds', label: 'Round toppers', subject: 'a sheet of twelve circles in a four-by-three grid with dashed cut lines, alternating "Eid Mubarak" lettering and a crescent with a star'},
        {key: 'shapes', label: 'Shaped toppers', subject: 'a sheet of shaped toppers outlined to cut: crescents, eight-pointed stars and small lanterns, the line "Cut out" printed once at the foot of the page and no lettering on the shapes'},
        {key: 'scallop', label: 'Scalloped', subject: 'a sheet of nine scalloped-edge circles, each lettered "Eid" in the centre with a ring of tiny dots around it'},
    ],
    styles: [
        {key: 'outline', label: 'Outline', style: 'bold black outlines on empty shapes, the drawings left to colour in', palette: 'black ink only', framing: 'The pieces spaced apart so each can be cut cleanly'},
        {key: 'geometric', label: 'Geometric', style: 'flat geometric print of eight-pointed stars and crescents with solid colour fills and crisp edges', palette: 'teal, gold and white', framing: 'The coloured pieces spaced apart on the white page'},
        {key: 'pastel', label: 'Pastel', style: 'soft flat illustration with rounded shapes and a gentle dotted border on every piece', palette: 'mint, blush, butter yellow and lavender', framing: 'The pieces spaced apart on the white page'},
    ],
    directions: ['Happy Eid', 'gold glitter', 'mini flags'],
    directionHint: 'try: Happy Eid',
    seo: {
        title: 'Eid cupcake toppers printable: rounds, shapes, scallops',
        metaDescription: 'Nine printable Eid cupcake toppers: round Eid Mubarak and crescent toppers, shaped crescents and stars, and scalloped Eid circles to print and cut.',
        h1: 'Eid cupcake toppers: round, shaped, scalloped',
        tagline: 'Nine print-and-cut sheets, three topper shapes each in outline, geometric and pastel finishes.',
        intro: 'Print-and-cut toppers for a family baking for an Eid party or a school Eid celebration. The round toppers alternate Eid Mubarak lettering with a crescent and star. The shaped toppers are crescents, eight-pointed stars and small lanterns, each outlined to cut. The scalloped toppers are circles lettered Eid, with a ring of tiny dots around each. Type a direction such as Happy Eid or gold glitter and the topper is drawn with it.',
        useCases: [
            'a family baking for Eid',
            'a school Eid celebration',
            'an Eid party table',
            'a plate of cupcakes for visitors',
        ],
        faq: [
            {q: 'How many toppers are on a sheet?', a: 'The round sheet has twelve toppers and the scalloped sheet has nine.'},
            {q: 'How is a topper used?', a: 'Cut it out and tape it to a toothpick, then push the toothpick into the cupcake.'},
            {q: 'What are the three styles?', a: 'Outline is black ink to colour in, geometric is a flat print of stars and crescents, and pastel is soft flat illustration with a dotted border.'},
            {q: 'Can I ask for a different look?', a: 'Yes. Type a direction such as mini flags and the toppers are drawn with it. That is a request, not a promised result.'},
        ],
    },
}
